import { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, Linking } from 'react-native';
import { Check, ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useUIStore } from '../../store/uiStore';
import { useEscapeToClose } from '../../utils/escapeStack';
import { Button } from '../common/Button';
import { Popover } from '../common/Popover';
import {
  FEATURE_ROW_DELETE_ICON_SIZE,
  FEATURE_ROW_ACTION_WIDTH,
} from './DefensePlanFeatureRow.web';
import { DEFENSE_STRATEGIES, strategyBadgeColors } from './defenseStrategies';
import { nextEventLabel } from '../../utils/eventCalendar';
import { planBundleOpportunityUrl, planEventShareUrl } from '../../utils/share';
import type { DefensePlanFeature, PlanBundle, PlanEvent } from '../../types';

// A bundle group with more members than this collapses by default — Glenn's
// "twenty homes" bundles would otherwise open as a wall of rows.
export const BUNDLE_COLLAPSE_THRESHOLD = 5;

export interface BundleWho {
  /** Pre-formatted summary, the same "N of M assigned" language the
   *  per-feature who-row uses (utils/planAssignmentSummary). */
  text: string;
  onAssign?: () => void;
}

interface DefensePlanBundleRowProps {
  bundle: PlanBundle;
  members: DefensePlanFeature[];
  selected: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onSelectHeader: () => void;
  /** Omit for a read-only header (the plan viewers) — no who line, no
   *  Assign action. */
  who?: BundleWho;
  /** Both omitted for a read-only header. */
  onRename?: (name: string) => void;
  onUngroup?: () => void;
  /** Opens the picker on this bundle ("Add or remove defenses"). */
  onEditMembers?: () => void;
  /** The bundle owns the companion that's open (Assign), so the header
   *  keeps a fill while the pointer is elsewhere. */
  active?: boolean;
  /** The soonest upcoming event on this bundle (Oct 2026). Shown as a line
   *  under the summary: "Sat, Nov 6 · 3 going". */
  nextEvent?: PlanEvent | null;
  /** Editor only: opens the bundle's events. Adds "Schedule an event" to the
   *  ⋯ menu and makes the next-event line open the events view. Without it
   *  (the plan viewers) the next-event line links to the public event page. */
  onScheduleEvent?: () => void;
  /** The plan this bundle is on. Viewers pass it so "Open for bids" can
   *  link to the public opportunity page. */
  planId?: string;
  /** Owner only: bids on this bundle, shown on the bids line. */
  bidCount?: number;
  /** Editor only: opens the bundle's bids view. Makes the bids line open it
   *  instead of the public page. */
  onOpenBids?: () => void;
  /** Editor only: adds "Open for bids" or "Close bids" to the ⋯ menu. */
  onToggleBids?: () => void;
  /** Editor only: adds "Find vendors" to the ⋯ menu (Nov 2026). */
  onFindVendors?: () => void;
  /** Editor only, while open: adds "Copy opportunity link" to the ⋯ menu. */
  onCopyOpportunityLink?: () => void;
  /** Owner only: the bundle's money, "Funded RB 12,000". Viewers can't read
   *  requests, so they only ever see the work-done date. */
  fundingText?: string | null;
  /** Editor only: adds "Mark work done" or "Reopen work" to the ⋯ menu. */
  onToggleWorkDone?: () => void;
}

/**
 * Header row for a bundle group (Sept 16 2026 — Glenn's "bundle steps" ask:
 * pick a set of homes/defenses, name the set, so one vendor bid and one
 * responsibility assignment can cover all of them). Shared by the editor
 * panel and both read-only viewers, the same split DefensePlanFeatureRow
 * uses — `who`/`onRename`/`onUngroup` all being omitted is what makes this
 * render as the viewer's read-only header.
 */
export function DefensePlanBundleRow({
  bundle,
  members,
  selected,
  expanded,
  onToggleExpanded,
  onSelectHeader,
  who,
  onRename,
  onUngroup,
  onEditMembers,
  active = false,
  nextEvent = null,
  onScheduleEvent,
  planId,
  bidCount = 0,
  onOpenBids,
  fundingText = null,
  onToggleWorkDone,
  onToggleBids,
  onFindVendors,
  onCopyOpportunityLink,
}: DefensePlanBundleRowProps) {
  const { colors, isDark } = useTheme();
  const { hovered, hoverProps: pointerProps } = useWebHover();
  const justAssigned = useUIStore((s) => s.justAssignedBundle?.id === bundle.id);
  const reducedMotion = useReducedMotion();
  // The pointer on the header lights every member on the map.
  const bundleId = bundle.id;
  const hoverProps =
    Platform.OS === 'web'
      ? {
          onPointerEnter: (e: unknown) => {
            (pointerProps as { onPointerEnter?: (e: unknown) => void }).onPointerEnter?.(e);
            useUIStore.getState().setHoveredPlanBundle?.(bundleId);
          },
          onPointerLeave: (e: unknown) => {
            (pointerProps as { onPointerLeave?: (e: unknown) => void }).onPointerLeave?.(e);
            useUIStore.getState().clearHoveredPlanBundle?.(bundleId);
          },
        }
      : pointerProps;
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(bundle.name);
  // "Bundle" on a selection makes the bundle and asks for its name: the
  // panel sets this id, and the header opens its rename field once.
  const renameRequested = useUIStore((st) => st.planBundleRenamingId === bundle.id);
  useEffect(() => {
    if (!renameRequested) return;
    useUIStore.getState().setPlanBundleRenamingId(null);
    if (onRename) setRenaming(true);
  }, [renameRequested, onRename]);
  useEffect(() => {
    setNameDraft(bundle.name);
  }, [bundle.name]);

  const homeCount = members.filter((m) => !!m.structureId).length;
  const isHomeBundle = members.length > 0 && homeCount === members.length;
  // Homes are counted once each: a bundle holding house hardening and
  // fireproof paint on the same house is one home, not two.
  const distinctHomes = new Set(members.map((m) => m.structureId)).size;
  const countLabel = isHomeBundle
    ? `${distinctHomes} ${distinctHomes === 1 ? 'home' : 'homes'}`
    : `${members.length} ${members.length === 1 ? 'defense' : 'defenses'}`;

  // Deduped, in first-appearance order — several homes sharing a strategy
  // shows one badge for it, not one per member.
  const strategies = Array.from(new Set(members.map((m) => m.strategy)));

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    setRenaming(false);
    if (!onRename || !trimmed || trimmed === bundle.name) return;
    onRename(trimmed);
  };

  const Chevron = expanded ? ChevronDown : ChevronRight;

  // Vendor bids (Oct 2026). The owner sees the count and opens the bids
  // view; a viewer of a shared plan sees "Open for bids" and gets the public
  // page, where a vendor can say they can help.
  const bidsOpen = !!bundle.bidsOpen;
  const bidsText = onOpenBids
    ? bidsOpen || bidCount > 0
      ? `${bidsOpen ? 'Open for bids' : 'Bids closed'} · ${
          bidCount === 0 ? 'No bids yet' : `${bidCount} ${bidCount === 1 ? 'bid' : 'bids'}`
        }`
      : null
    : bidsOpen && planId
      ? 'Open for bids'
      : null;
  const workDoneAt = bundle.workDoneAt;
  const moneyText =
    [
      fundingText,
      workDoneAt
        ? `Work done ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(workDoneAt.toDate())}`
        : null,
    ]
      .filter(Boolean)
      .join(' · ') || null;

  const cancelRename = () => {
    setNameDraft(bundle.name);
    setRenaming(false);
  };
  // On the escape stack, so the first Escape cancels the rename even while
  // an undo toast (which also claims Escape, and whose capture-phase close
  // swallows the key) is up: the field registers after the toast, so it is
  // the top layer. Found in the Oct 15 2026 gate: Bundle from the bar, then
  // Escape, left the name field open until a second press.
  useEscapeToClose(renaming, cancelRename);

  return (
    <View
      testID={`plan-bundle-${bundle.id}`}
      style={[
        styles.container,
        {
          backgroundColor:
            active || selected
              ? colors.pressedSurface
              : hovered
                ? colors.hoverSurface
                : 'transparent',
        },
      ]}>
      <View style={styles.topRow}>
        <Pressable
          onPress={onToggleExpanded}
          testID={`plan-bundle-${bundle.id}-toggle`}
          accessibilityRole="button"
          aria-expanded={expanded}
          accessibilityLabel={expanded ? `Collapse ${bundle.name}` : `Expand ${bundle.name}`}
          style={[styles.chevronButton, CURSOR_POINTER]}>
          <Chevron size={CHEVRON_SIZE} color={colors.textTertiary} />
        </Pressable>
        {/* Sept 28 polish: the name alone on the first line, one quiet
            summary line under it ("2 defenses · None assigned yet", with the
            strategy badges only while collapsed, since the member rows show
            them when open), then the same small Assign as the members and a
            ⋯ for Rename and Unbundle in the column where members keep
            their ⋯. */}
        <Pressable
          onPress={onSelectHeader}
          {...hoverProps}
          testID={`plan-bundle-${bundle.id}-select`}
          accessibilityRole="button"
          accessibilityLabel={`${bundle.name}, ${countLabel}`}
          style={[styles.headerMain, CURSOR_POINTER]}>
          {renaming ? (
            // The responder-capture trick DefensePlanFeatureRow's action
            // buttons use to keep this press off the header's own
            // onSelectHeader.
            <View
              style={styles.renameField}
              // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
              dataSet={{ planNodrag: '' }}
              onStartShouldSetResponder={() => true}
              onResponderRelease={(e) => e.stopPropagation()}>
              <TextInput
                value={nameDraft}
                onChangeText={setNameDraft}
                onBlur={commitRename}
                onSubmitEditing={commitRename}
                onKeyPress={(e) => {
                  if (e.nativeEvent.key === 'Escape') cancelRename();
                }}
                autoFocus
                selectTextOnFocus
                maxLength={60}
                testID={`plan-bundle-${bundle.id}-rename-input`}
                accessibilityLabel="Bundle name"
                style={[styles.nameInput, { color: colors.text, borderColor: colors.borderFocus }]}
              />
            </View>
          ) : (
            <Text style={[styles.name, { color: colors.text }]} numberOfLines={1}>
              {bundle.name}
            </Text>
          )}
          {/* Oct 21: one fact per line, every line on the name's left edge.
              The badges and the count share a line that never wraps; who's
              assigned and the next event each get their own line below, so
              a long status wraps on its own instead of folding up beside
              the badges. */}
          <View style={styles.subRow}>
            {!expanded && (
              <View style={styles.badgeRow}>
                {strategies.map((strategy) => {
                  const def = DEFENSE_STRATEGIES[strategy];
                  const badge = strategyBadgeColors(strategy, isDark);
                  return (
                    <View key={strategy} style={[styles.badge, { backgroundColor: badge.tint }]}>
                      <def.icon size={11} color={badge.icon} />
                    </View>
                  );
                })}
              </View>
            )}
            <Text style={[styles.summary, { color: colors.textTertiary }]} numberOfLines={1}>
              {countLabel}
            </Text>
          </View>
          {who?.text ? (
            // Two lines, so a team's names ("Laura Dyberg (leader), Jane
            // Doe") wrap beside the Assign button instead of losing the
            // leader tag to an ellipsis.
            <Text style={[styles.status, { color: colors.textTertiary }]} numberOfLines={2}>
              {/* While just assigned, the text remounts on change and fades
                  in, as the feature row's does. */}
              <Text
                key={justAssigned ? who.text : undefined}
                style={{ color: colors.textSecondary }}
                {...(justAssigned ? { dataSet: { rediAnim: 'fade-in' } } : {})}>
                {who.text}
              </Text>
            </Text>
          ) : null}
          {nextEvent ? (
            <Text
              // A viewer opens the public page in a new tab, so the map stays
              // put; the owner gets the events panel.
              onPress={(e) => {
                e.stopPropagation();
                if (onScheduleEvent) onScheduleEvent();
                else void Linking.openURL(planEventShareUrl(nextEvent.id));
              }}
              testID={`plan-bundle-${bundle.id}-next-event`}
              accessibilityRole="link"
              accessibilityLabel={`Next event for ${bundle.name}: ${nextEventLabel(nextEvent)}`}
              // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
              dataSet={{ planNodrag: '' }}
              numberOfLines={1}
              // react-native-web gives a pressable Text the pointer cursor.
              style={[styles.status, { color: colors.textSecondary }]}>
              {nextEventLabel(nextEvent)}
            </Text>
          ) : null}
          {bidsText ? (
            <Text
              onPress={(e) => {
                e.stopPropagation();
                if (onOpenBids) onOpenBids();
                else if (planId) void Linking.openURL(planBundleOpportunityUrl(planId, bundle.id));
              }}
              testID={`plan-bundle-${bundle.id}-bids-line`}
              accessibilityRole="link"
              accessibilityLabel={`${bundle.name}: ${bidsText}`}
              // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
              dataSet={{ planNodrag: '' }}
              numberOfLines={1}
              style={[styles.status, { color: colors.textSecondary }]}>
              {bidsText}
            </Text>
          ) : null}
          {moneyText ? (
            <Text
              onPress={
                onOpenBids
                  ? (e) => {
                      e.stopPropagation();
                      onOpenBids();
                    }
                  : undefined
              }
              testID={`plan-bundle-${bundle.id}-funding-line`}
              accessibilityRole={onOpenBids ? 'link' : undefined}
              accessibilityLabel={`${bundle.name}: ${moneyText}`}
              // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
              dataSet={{ planNodrag: '' }}
              numberOfLines={1}
              style={[styles.status, { color: colors.textSecondary }]}>
              {moneyText}
            </Text>
          ) : null}
        </Pressable>
        {who?.onAssign && (
          <View
            style={styles.trailing}
            // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
            dataSet={{ planNodrag: '' }}
            onStartShouldSetResponder={() => true}
            onResponderRelease={(e) => e.stopPropagation()}>
            <Button
              title="Assign"
              onPress={who.onAssign}
              variant="secondary"
              size="xs"
              testID={`plan-bundle-${bundle.id}-assign`}
            />
          </View>
        )}
        {(onRename ||
          onUngroup ||
          onEditMembers ||
          onScheduleEvent ||
          onToggleBids ||
          onFindVendors ||
          onToggleWorkDone) && (
          <BundleActionsMenu
            bundleId={bundle.id}
            bundleName={bundle.name}
            onRename={onRename ? () => setRenaming(true) : undefined}
            onEditMembers={onEditMembers}
            onScheduleEvent={onScheduleEvent}
            bidsOpen={bidsOpen}
            onToggleBids={onToggleBids}
            onFindVendors={onFindVendors}
            onCopyOpportunityLink={bidsOpen ? onCopyOpportunityLink : undefined}
            onToggleWorkDone={onToggleWorkDone}
            workDone={!!workDoneAt}
            onUngroup={onUngroup}
          />
        )}
      </View>
      {justAssigned && (
        <View
          pointerEvents="none"
          testID={`plan-bundle-${bundle.id}-assigned`}
          style={styles.assignedCheck}
          // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
          dataSet={reducedMotion ? undefined : { rediAnim: 'check-out' }}>
          <Check size={12} color={colors.success} />
        </View>
      )}
    </View>
  );
}

/** Rename, Add or remove defenses and Unbundle, behind a ⋯ in the column where member rows keep
 *  their delete, so the bundle's actions line up with its members'. */
function BundleActionsMenu({
  bundleId,
  bundleName,
  onRename,
  onEditMembers,
  onScheduleEvent,
  bidsOpen,
  onToggleBids,
  onFindVendors,
  onCopyOpportunityLink,
  onToggleWorkDone,
  workDone,
  onUngroup,
}: {
  bundleId: string;
  bundleName: string;
  onRename?: () => void;
  onEditMembers?: () => void;
  onScheduleEvent?: () => void;
  bidsOpen: boolean;
  onToggleBids?: () => void;
  onFindVendors?: () => void;
  onCopyOpportunityLink?: () => void;
  onToggleWorkDone?: () => void;
  workDone?: boolean;
  onUngroup?: () => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<View>(null);
  const close = useCallback(() => setOpen(false), []);
  const items = [
    onRename && { key: 'rename', label: 'Rename', onPress: onRename, danger: false },
    onEditMembers && {
      key: 'edit',
      label: 'Add or remove defenses',
      onPress: onEditMembers,
      danger: false,
    },
    onScheduleEvent && {
      key: 'event',
      label: 'Schedule an event',
      onPress: onScheduleEvent,
      danger: false,
    },
    onToggleBids && {
      key: bidsOpen ? 'bids-close' : 'bids-open',
      label: bidsOpen ? 'Close bids' : 'Open for bids',
      onPress: onToggleBids,
      danger: false,
    },
    onFindVendors && {
      key: 'find-vendors',
      label: 'Find vendors',
      onPress: onFindVendors,
      danger: false,
    },
    onCopyOpportunityLink && {
      key: 'bids-link',
      label: 'Copy opportunity link',
      onPress: onCopyOpportunityLink,
      danger: false,
    },
    onToggleWorkDone && {
      key: workDone ? 'work-reopen' : 'work-done',
      label: workDone ? 'Reopen work' : 'Mark work done',
      onPress: onToggleWorkDone,
      danger: false,
    },
    onUngroup && { key: 'ungroup', label: 'Unbundle', onPress: onUngroup, danger: false },
  ].filter(Boolean) as { key: string; label: string; onPress: () => void; danger: boolean }[];
  return (
    <View
      style={styles.trailing}
      // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
      dataSet={{ planNodrag: '' }}
      onStartShouldSetResponder={() => true}
      onResponderRelease={(e) => e.stopPropagation()}>
      <Pressable
        ref={anchorRef}
        onPress={() => setOpen((v) => !v)}
        testID={`plan-bundle-${bundleId}-more`}
        accessibilityRole="button"
        accessibilityLabel={`More actions for ${bundleName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        {...hoverProps}
        style={[styles.moreButton, CURSOR_POINTER]}>
        <MoreHorizontal
          size={FEATURE_ROW_DELETE_ICON_SIZE}
          color={hovered ? colors.text : colors.textSecondary}
        />
      </Pressable>
      <Popover
        visible={open}
        anchorRef={anchorRef}
        onClose={close}
        placement="bottom-end"
        gap={SPACING.xxs}
        testID={`plan-bundle-${bundleId}-menu`}
        accessibilityLabel={`${bundleName} actions`}>
        <View style={styles.menu}>
          {items.map((item) => (
            <MenuRow
              key={item.key}
              label={item.label}
              testID={`plan-bundle-${bundleId}-${item.key}`}
              onPress={() => {
                setOpen(false);
                item.onPress();
              }}
            />
          ))}
        </View>
      </Popover>
    </View>
  );
}

function MenuRow({
  label,
  onPress,
  testID,
}: {
  label: string;
  onPress: () => void;
  testID: string;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="menuitem"
      {...hoverProps}
      style={[
        styles.menuRow,
        CURSOR_POINTER,
        { backgroundColor: hovered ? colors.hoverSurface : 'transparent' },
      ]}>
      <Text style={[styles.menuLabel, { color: colors.text }]}>{label}</Text>
    </Pressable>
  );
}

const CHEVRON_SIZE = 16;
// The name's offset from the header's padding edge: the chevron button
// (icon plus its padding) and the row gap. The sub row starts here too.
const NAME_OFFSET = CHEVRON_SIZE + SPACING.xxs * 2 + SPACING.xs;
/** Where the bundle name starts, from the card's inner edge. The card
 *  indents its members to put their badges on this line. */
export const BUNDLE_NAME_INSET = SPACING.xs + NAME_OFFSET;
/** The bundle card's padding right of its member rows, so a member's fill
 *  sits inset on both sides. With a nested row's own SPACING.xxs of right
 *  padding, a member's Assign ends up SPACING.xxs further in than a card
 *  row's would, and the header's Assign slot (assignSlot) moves in to
 *  match. */
export const BUNDLE_MEMBERS_RIGHT_INSET = SPACING.xs;

const styles = StyleSheet.create({
  // No border of its own — the card border lives on the group wrapper
  // (bundleGroup, in DefensePlanPanel.web.tsx / DefensePlanViewer.web.tsx /
  // DefensePlanViewerCard.web.tsx) so a bundle with expanded members reads
  // as one bordered card, not a bordered header sitting above its members.
  container: {
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.xs,
  },
  // Top-aligned: the chevron sits on the name's line, not centered on the
  // two-line block; Assign and ⋯ center themselves on the whole header.
  // Same corner and insets as DefensePlanFeatureRow's assignedCheck.
  assignedCheck: {
    position: 'absolute',
    top: SPACING.xxs,
    right: SPACING.xxs,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.xs,
  },
  // Centers the 14px chevron (plus padding) on the label's 16px line.
  chevronButton: {
    padding: SPACING.xxs,
    marginTop: -1,
  },
  headerMain: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    ...TYPOGRAPHY.label,
  },
  renameField: {
    alignSelf: 'flex-start',
  },
  nameInput: {
    ...TYPOGRAPHY.label,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.xs,
    paddingHorizontal: SPACING.xxs,
    paddingVertical: 1,
    minWidth: 120,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginTop: 1,
  },
  summary: {
    ...TYPOGRAPHY.caption,
    flexShrink: 1,
  },
  status: {
    ...TYPOGRAPHY.caption,
    marginTop: 1,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 2,
  },
  badge: {
    width: 16,
    height: 16,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  trailing: {
    alignSelf: 'center',
  },
  // Same box as a member row's ⋯ (15px icon plus SPACING.xxs padding each
  // side = FEATURE_ROW_ACTION_WIDTH, 19px), and the same right offset. From
  // the card's inner edge a member's ⋯ ends BUNDLE_MEMBERS_RIGHT_INSET (4)
  // plus the nested row's SPACING.xxs (2) = 6px in. The header ends
  // container padding SPACING.xs (4) plus marginRight SPACING.xxs (2) = 6px
  // in, so the ⋯ boxes match. Both rows put SPACING.xs (4) between Assign
  // and ⋯, so Assign matches too (the 1px card border is shared).
  moreButton: {
    width: FEATURE_ROW_ACTION_WIDTH,
    // The same padding as a member's icon box, so this ⋯ sits over theirs.
    alignItems: 'flex-end',
    paddingHorizontal: SPACING.xxs,
    paddingVertical: SPACING.xxs,
    marginRight: SPACING.xxs,
    borderRadius: BORDER_RADIUS.sm,
  },
  menu: {
    padding: SPACING.xs,
    minWidth: 160,
  },
  menuRow: {
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
  },
  menuLabel: {
    ...TYPOGRAPHY.bodySmall,
  },
});
